import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Eye, LogOut } from 'lucide-react';
import { useAuthStore } from '../stores/auth.store';
import type { TFunction } from 'i18next';

/** Kalan süreyi `27 dk` / `4 dk 12 sn` biçiminde formatla. */
function formatRemaining(ms: number, t: TFunction<'components'>): string {
  if (ms <= 0) return t('impersonation.expired');
  const totalSec = Math.floor(ms / 1000);
  const min = Math.floor(totalSec / 60);
  const sec = totalSec % 60;
  if (min >= 5) return t('impersonation.minutes', { min });
  if (min >= 1) return t('impersonation.minutesSeconds', { min, sec });
  return t('impersonation.seconds', { sec });
}

/**
 * Sprint 34 — impersonation sticky banner.
 *
 * Sadece platform staff bir müşteriyi salt-okunur görüntülerken (isImpersonating)
 * DashboardLayout'un en üstünde görünür. BRANDBOOK platform aksanı: magenta/violet bg,
 * ink text. Token exp claim'inden geri sayım yapar; süre dolunca otomatik çıkar.
 */
export function ImpersonationBanner() {
  const { t } = useTranslation('components');
  const isImpersonating = useAuthStore((s) => s.isImpersonating);
  const companyName = useAuthStore((s) => s.impersonatedCompanyName);
  const expiresAt = useAuthStore((s) => s.impersonationExpiresAt);
  const exit = useAuthStore((s) => s.exitImpersonation);

  const [remaining, setRemaining] = useState<number>(() => (expiresAt ? expiresAt - Date.now() : 0));

  useEffect(() => {
    if (!isImpersonating || !expiresAt) return;
    const tick = () => {
      const left = expiresAt - Date.now();
      setRemaining(left);
      if (left <= 0) exit();
    };
    tick();
    const id = window.setInterval(tick, 1000);
    return () => window.clearInterval(id);
  }, [isImpersonating, expiresAt, exit]);

  if (!isImpersonating) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      className="sticky top-0 z-50 flex items-center justify-between gap-3 bg-[#a21caf] px-4 py-2 text-custel-ink md:px-6"
    >
      <div className="flex min-w-0 items-center gap-2 text-sm">
        <Eye size={16} aria-hidden="true" className="shrink-0" />
        <span className="min-w-0 truncate font-medium">
          {t('impersonation.modeLabel')} —{' '}
          <span className="font-semibold">{companyName ?? t('impersonation.fallbackCompany')}</span>{' '}
          {t('impersonation.readOnlyViewing')}
        </span>
        <span aria-hidden="true" className="hidden sm:inline">
          ·
        </span>
        <span className="hidden whitespace-nowrap font-mono tabular-nums sm:inline">
          {t('impersonation.remaining', { remaining: formatRemaining(remaining, t) })}
        </span>
      </div>
      <button
        type="button"
        onClick={exit}
        className="inline-flex shrink-0 items-center gap-1.5 rounded-custel-md bg-custel-ink/90 px-3 py-1 text-xs font-medium text-white transition-colors hover:bg-custel-ink focus:outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-custel-ink"
      >
        <LogOut size={14} aria-hidden="true" />
        {t('impersonation.exit')}
      </button>
    </div>
  );
}
